import React, { useEffect } from "react";
import DatePicker from "react-datepicker";
import cx from "classnames";
import PropTypes from "prop-types";
import ReactInputMask from "react-input-mask";
import { range } from "lodash";
import moment from "moment";
import { useTranslation } from "react-i18next";
import CalendarIcon from "@/assets/images/icons/calendar.svg";

import "react-datepicker/dist/react-datepicker.css";
import "./style.scss";

const DatePickerComponent = ({
	label,
	isRequired,
	variant = "small",
	inputClassname,
	className,
	size = "normal",
	format,
	popperPlacement = "bottom-end",
	calendarStartDay = 1,
	placeholder = "YYYY-MM-DD",
	popperClassName,
	today,
	type,
	minDate,
	handleBlur = () => {},
	handleChange = () => {},
	value,
	showTimeSelect,
	timeIntervals,
	selectToday = false,
	...props
}) => {
	const { t } = useTranslation();

	const selected = value ? moment(value).toDate() : selectToday ? new Date() : undefined;
	const headerDate = selected ? selected : new Date();

	const years = range(headerDate.getFullYear() - 20, headerDate.getFullYear() + 20, 1);
	const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

	useEffect(() => {
		if (selectToday && !value) {
			handleChange(new Date())
		}
	}, []);

	const getMask = () => {
		return format
			.replaceAll("y", "9")
			.replaceAll("M", "9")
			.replaceAll("d", "9")
			.replaceAll("H", "9")
			.replaceAll("s", "9")
			.replaceAll("m", "9");
	};

	return (
		<div className={`${className} form-group day-picker day-picker-${size}`}>
			{label && (
				<label className="form-field__label">
					{label}
				</label>
			)}
			<div className="day-picker__container">
				{type === "range" ? (
					<>
						<DatePicker
							className={`${cx(inputClassname, variant)} picker`}
							selectsRange={true}
							dateFormat="dd/MM/yyyy"
							showYearDropdown
							showMonthDropdown
							placeholderText={"DD.MM.YYYY - DD.MM.YYYY"}
							startDate={value[0] ? moment(value[0]).toDate() : value[0]}
							endDate={value[1] ? moment(value[1]).toDate() : value[1]}
							dropdownMode="select"
							popperClassName={`${cx("z-index-99 border-r-5 ", popperClassName)}`}
							portalId="main-content"
							peekNextMonth
							onChange={value => {
								handleChange(value);
							}}
							{...props}
						/>
						{value.length > 0 && value[0] !== null && (
							<span
								className="datepickerClose"
								onClick={() => {
									handleChange([]);
								}}>
								x
							</span>
						)}
					</>
				) : (
					<>
						<DatePicker
							minDate={minDate}
							showFullMonthYearPicker
							placeholderText={placeholder}
							calendarStartDay={calendarStartDay}
							popperPlacement={popperPlacement}
							dateFormat={format}
							selected={selected}
							customInput={<ReactInputMask className="dark:bg-dark-1" mask={getMask()} />}
							className={cx(inputClassname, variant)}
							popperClassName={cx("z-index-99 border-r-5", popperClassName)}
							portalId="main-content"
							todayButton={
								today && (
									<button
										type="secondary"
										style={{ margin: "0 auto", borderRadius: "5px", minWidth: "120px" }}>
										{t("Today")}
									</button>
								)
							}
							onChange={date => {
								handleChange(date);
							}}
							onBlur={() => {
								setTimeout(() => {
									selected && handleBlur();
								}, 10);
							}}
							renderCustomHeader={({
								date,
								changeYear,
								changeMonth,
								decreaseMonth,
								increaseMonth,
								prevMonthButtonDisabled,
								nextMonthButtonDisabled
							}) => (
								<div style={{ margin: 10, display: "flex", justifyContent: "center" }}>
									<button onClick={decreaseMonth} disabled={prevMonthButtonDisabled} className="react-datepicker__prev">
										{"<"}
									</button>
									<select className="dark:bg-dark-1" value={date.getFullYear()} onChange={({ target: { value } }) => changeYear(value)}>
										{years.map(option => (
											<option key={option} value={option}>
												{option}
											</option>
										))}
									</select>

									<select
										className="dark:bg-dark-1"
										value={months[date.getMonth()]}
										onChange={({ target: { value } }) => changeMonth(months.indexOf(value))}>
										{months.map(option => (
											<option key={option} value={option}>
												{t(option)}
											</option>
										))}
									</select>

									<button onClick={increaseMonth} disabled={nextMonthButtonDisabled} className="react-datepicker__next">
										{">"}
									</button>
								</div>
							)}
							timeCaption="Выберите время"
							timeFormat="HH:mm"
							showTimeSelect={showTimeSelect}
							timeIntervals={timeIntervals}
							{...props}
						/>
						<img className={cx("day-picker__img", variant)} src={CalendarIcon} alt="Choose date" />
					</>
				)}
			</div>
		</div>
	);
};

DatePickerComponent.propTypes = {
	label: PropTypes.string,
	isRequired: PropTypes.bool,
	variant: PropTypes.oneOf(["extra-small", "small", "normal"]),
	inputClassname: PropTypes.string,
	className: PropTypes.string,
	format: PropTypes.string,
	popperPlacement: PropTypes.string,
	calendarStartDay: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7]),
	placeholder: PropTypes.string,
	popperClassName: PropTypes.string,
	today: PropTypes.bool,
	disabled: PropTypes.bool,
	timeIntervals: PropTypes.number,
	showTimeSelect: PropTypes.bool
};

DatePickerComponent.defaultProps = {
	label: "",
	isRequired: true,
	variant: "small",
	inputClassname: "",
	className: "",
	format: "yyyy-MM-dd",
	popperPlacement: "bottom-end",
	calendarStartDay: 1,
	placeholder: "dd.MM.yyyy",
	popperClassName: "",
	today: true,
	disabled: false,
	timeIntervals: 15,
	showTimeSelect: false
};

export default DatePickerComponent;
